/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2026 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { getSingletonFunction } from '../core/ProviderUtil';
import { logging } from './LoggingAPI';

/** Parsed AJO proposition `characteristics.eventToken` (Base64 JSON). */
export interface EventTokenPayload {
  messageExecution?: {
    messageExecutionID?: string;
    messageID?: string;
    messagePublicationID?: string;
    messageType?: string;
    campaignID?: string;
    campaignVersionID?: string;
    campaignActionID?: string;
  };
  messageProfile?: {
    messageProfileID?: string;
    channel?: {
      _id?: string;
      _type?: string;
    };
  };
}

export interface IProposition {
  campaignId?: string;
  campaignVersionId?: string;
  campaignActionId?: string;
  treatmentId?: string;
  type?: string;
  content?: object;
  raw: object;
  displayed(): void;
}

export interface AJOOptions {
  ctxtKeyVals?: object;
  ctxtObj?: object;
  strictlyNecessary?: boolean;
  timeout?: number;
}

export interface IAJOAPI {
  ready: () => Promise<IAJOAPI>;
  fetchProposition: (surface: string, options: AJOOptions) => Promise<IProposition>;
  fetchPropositions: (surfaces: string[], options: AJOOptions) => Promise<object>;
}

class AJOAPI implements IAJOAPI {
  static getInstance: IAJOAPI;

  logger: any;
  timeout: number;

  constructor() {
    this.logger = logging.getLogger('AJOAPI');
    this.timeout = 5000;
  }

  ready() {
    return Promise.resolve(this);
  }

  async fetchProposition(surface: string, options: AJOOptions = {}) {
    return this.fetchPropositions([surface], options).then(propositions => propositions[surface] ?? null);
  }

  async fetchPropositions(surfaces: string[], options: AJOOptions = {}) {
    if (options.ctxtKeyVals && options.ctxtObj) {
      throw new Error('ctxtKeyVals and ctxtObj must not be passed together');
    }

    return new Promise<object>((resolve, reject) => {
      let timeoutId: NodeJS.Timeout | null = null;
      let timedout = false;

      timeoutId = setTimeout(() => {
        timedout = true;
        reject(new Error('AJO fetchPropositions timeout'));
      }, options.timeout ?? this.timeout);

      window.__satelliteLoadedPromise.then(s => s.track('propositionFetch', {
        personalization: {
          surfaces,
        },
        strictlyNecessary: options.strictlyNecessary ?? false,
        xdm: options.ctxtObj ?? this.inflate(options.ctxtKeyVals),
        done: (res: any, err: any) => {
          if (!timedout) {
            clearTimeout(timeoutId);
          }
          const propositions = {};
          if (err) {
            const msg = `Failed to load proposition from AJO: ${err}`;
            this.logger.error(msg);
            reject(new Error(msg));
          } else if (!res || !res.propositions || res.propositions.length === 0) {
            // No campaign was found for the active user
            resolve(propositions);
          } else {
            res.propositions.forEach((proposition: any) => {
              const parsedEventJson = this.parseEventTokenPayload(proposition.scopeDetails?.characteristics?.eventToken);
              // eslint-disable-next-line no-unused-expressions
              proposition.scopeDetails?.activity?.matchedSurfaces?.forEach((surfaceName: string) => {
                propositions[surfaceName] = {
                  campaignId: parsedEventJson?.messageExecution?.campaignID,
                  campaignVersionId: parsedEventJson?.messageExecution?.campaignVersionID,
                  campaignActionId: parsedEventJson?.messageExecution?.campaignActionID,
                  treatmentId: proposition.scopeDetails?.strategies?.[0]?.treatmentID,
                  type: proposition.items?.[0]?.schema,
                  content: proposition.items?.[0]?.data?.content,
                  raw: proposition,
                  displayed: () => window._satellite.track('propositionDisplay', [proposition]),
                };
              });
            });
          }
          resolve(propositions);
        },
      }));
    });
  }

  private inflate(flat: object) {
    if (!flat) {
      return {};
    }
    const inflated = {};
    const entries = Object.entries(flat);
    for (let i = 0; i < entries.length; i++) {
      const parts = entries[i][0].split('.');
      let current = inflated;
      for (let j = 0; j < parts.length; j++) {
        const part = parts[j];
        if (j === parts.length - 1) {
          current[part] = entries[i][1];
        } else {
          current[part] ??= {};
          current = current[part];
        }
      }
    }
    return inflated;
  }

  private parseEventTokenPayload(str: string | undefined | null): EventTokenPayload {
    if (!str) {
      return {};
    }
    try {
      return JSON.parse(atob(str)) as EventTokenPayload;
    } catch {
      return {};
    }
  }
}

// This allows for providers.x().then() to be called before providers.x(config).
AJOAPI.getInstance = getSingletonFunction(AJOAPI);
export default AJOAPI;
